import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Brain, Eye, Filter, Target, Building2, Scale, Trophy, Flame, Rocket, Globe, RefreshCw, Search, Crosshair, Zap, TrendingUp, CheckCircle, Car, Phone, Bot, Smartphone, Users, Star, MessageSquare } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "What You Focus On,",
      highlight: "You Find",
      subhead: "The Next Chapter of Calldrip"
    }
  },
  {
    id: 2,
    type: "hook",
    content: {
      label: "EVER NOTICE THIS?",
      headline: "Something Interesting Happens...",
      examples: [
        { icon: "car", text: "You think about a new car... suddenly it's everywhere" },
        { icon: "problem", text: "A problem shows up... then more follow" },
        { icon: "win", text: "A win happens... and momentum builds" },
      ],
      punchline: "What you focus on expands."
    }
  },
  {
    id: 3,
    type: "filter",
    content: {
      label: "THE SCIENCE",
      headline: "Your Brain Has a Filter",
      points: [
        "You take in millions of inputs every second",
        "Your brain decides what matters",
        "It highlights what you focus on"
      ],
      concept: "Reticular Activating System (RAS)",
      description: "The part of your brain that filters reality based on what you think is important"
    }
  },
  {
    id: 4,
    type: "howItWorks",
    content: {
      label: "KEEP IT SIMPLE",
      headline: "Here's How It Works",
      steps: [
        { action: "Focus on something", result: "You see more of it" },
        { action: "Think it matters", result: "It gets prioritized" },
        { action: "Repeat it", result: "It becomes your reality" },
      ],
      punchline: "Your focus becomes your experience"
    }
  },
  {
    id: 5,
    type: "appliesTo",
    content: {
      label: "THIS ISN'T JUST PERSONAL",
      headline: "This Applies to Us",
      items: [
        { icon: "users", label: "Teams do this" },
        { icon: "building", label: "Companies do this" },
        { icon: "target", label: "Leaders do this" },
      ],
      message: "What we focus on as a company becomes what we see, talk about, and act on"
    }
  },
  {
    id: 6,
    type: "twoLenses",
    content: {
      label: "TWO WAYS TO LOOK AT IT",
      headline: "Same Company. Two Lenses.",
      lensA: {
        title: "Lens 1",
        color: "#ef4444",
        items: ["Risk", "Fear", "\"What if we lose...\""]
      },
      lensB: {
        title: "Lens 2", 
        color: "#22c55e",
        items: ["Opportunity", "Growth", "\"What can we build...\""]
      },
      punchline: "Both exist. We choose which one we feed."
    }
  },
  {
    id: 7,
    type: "facts",
    content: {
      label: "LET'S TALK REALITY",
      headline: "The Facts Speak",
      stats: [
        { value: "15+", label: "Years in Business" },
        { value: "Multiple", label: "Market Shifts Survived" },
        { value: "COVID-19", label: "Pandemic... Still Here" },
        { value: "Evolved", label: "And Kept Growing" },
      ],
      quote: "That doesn't happen by accident."
    }
  },
  {
    id: 8,
    type: "whatWeBuilt",
    content: {
      label: "THIS COMPANY ISN'T STANDING STILL",
      headline: "What We've Built",
      achievements: [
        { icon: "zap", text: "Speed-to-lead leader" },
        { icon: "phone", text: "Call tracking + AI scoring" },
        { icon: "car", text: "Proven dealership impact" },
        { icon: "users", text: "Strong partnerships" },
      ],
      punchline: "We didn't survive... we adapted and improved"
    }
  },
  {
    id: 9,
    type: "momentum",
    content: {
      label: "WHAT'S HAPPENING RIGHT NOW",
      headline: "Current Momentum",
      items: [
        { icon: "smartphone", label: "New Mobile App", desc: "Performance in your pocket" },
        { icon: "bot", label: "AI Virtual Assistants", desc: "24/7 lead engagement" },
        { icon: "rocket", label: "Sidekick Innovation", desc: "Next-gen sales support" },
        { icon: "chart", label: "Multi-Department Scorecards", desc: "Full visibility" },
      ],
      punchline: "This is what growth looks like in real time"
    }
  },
  {
    id: 10,
    type: "opportunity",
    content: {
      label: "WHAT'S IN FRONT OF US",
      headline: "Opportunity Is Everywhere",
      categories: [
        { 
          title: "New Industries",
          items: ["Kubota / Equipment Dealers", "Service Departments", "Multi-Location Groups"]
        },
        {
          title: "New Use Cases", 
          items: ["Review Generation", "Follow-Up Automation", "Lifecycle Communication"]
        }
      ],
      punchline: "We are not out of opportunities - we are early in them"
    }
  },
  {
    id: 11,
    type: "reframe",
    content: {
      label: "SAME REALITY. DIFFERENT FOCUS.",
      headline: "Reframe",
      columns: [
        {
          focus: "Problems",
          result: "We find more problems",
          color: "#ef4444"
        },
        {
          focus: "Limitations",
          result: "We find more limits",
          color: "#f97316"
        },
        {
          focus: "Wins",
          result: "We find more wins",
          color: "#22c55e"
        },
        {
          focus: "Opportunities",
          result: "We find ways to grow",
          color: "#3b82f6"
        }
      ]
    }
  },
  {
    id: 12,
    type: "question",
    content: {
      label: "QUESTION THE ROOM",
      headline: "What Are You Looking For?",
      questions: [
        "What are you noticing daily?",
        "What are you talking about?",
        "What are you expecting to happen?"
      ],
      punchline: "Because that's exactly what you'll keep finding"
    }
  },
  {
    id: 13,
    type: "intentional",
    content: {
      label: "SHIFT THE HABIT",
      headline: "Let's Be Intentional",
      actions: [
        { icon: "trophy", text: "Focus on wins daily" },
        { icon: "message", text: "Talk about opportunities" },
        { icon: "trending", text: "Recognize momentum" },
      ],
      punchline: "Train your focus like it matters - because it does"
    }
  },
  {
    id: 14,
    type: "closing",
    content: {
      setup: "We've Already Proven Something",
      statement: "We know how to survive.",
      question: "Now the question is:",
      highlight: "Are we focused on surviving...",
      answer: "or scaling?"
    }
  }
];

// Icon component mapper
const IconComponent = ({ name, size = 24, className = "" }) => {
  const icons = {
    brain: Brain,
    eye: Eye,
    filter: Filter,
    target: Target,
    building: Building2,
    scale: Scale,
    trophy: Trophy,
    flame: Flame,
    rocket: Rocket,
    globe: Globe,
    refresh: RefreshCw,
    search: Search,
    crosshair: Crosshair,
    zap: Zap,
    trending: TrendingUp,
    check: CheckCircle,
    car: Car,
    phone: Phone,
    bot: Bot,
    smartphone: Smartphone,
    users: Users,
    star: Star,
    message: MessageSquare,
    chart: TrendingUp,
  };
  const Icon = icons[name] || Target;
  return <Icon size={size} className={className} />;
};

// Slide Renderers
const TitleSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-12 opacity-90" />
    <h1 className="text-5xl md:text-7xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-7xl font-bold text-orange-500 mb-6">
      {content.highlight}
    </h1>
    <h2 className="text-2xl md:text-3xl font-light text-cyan-400">
      {content.subhead}
    </h2>
  </div>
);

const HookSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="space-y-6 flex-1">
      {content.examples.map((example, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
          <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <IconComponent name={example.icon === "car" ? "car" : example.icon === "problem" ? "zap" : "trophy"} size={24} className="text-cyan-400" />
          </div>
          <span className="text-white/90 text-lg">{example.text}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-3xl font-bold text-orange-500">{content.punchline}</p>
    </div>
  </div>
);

const FilterSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center mb-8 border-2 border-purple-500/50">
        <Brain size={64} className="text-purple-400" />
      </div>
      <div className="space-y-4 mb-8 max-w-2xl">
        {content.points.map((point, i) => (
          <div key={i} className="flex items-center gap-3 justify-center">
            <div className="w-2 h-2 rounded-full bg-cyan-400" />
            <span className="text-white/80 text-lg">{point}</span>
          </div>
        ))}
      </div>
      <div className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-xl p-6 border border-purple-500/30 max-w-xl">
        <p className="text-2xl font-bold text-white text-center mb-2">{content.concept}</p>
        <p className="text-white/60 text-center">{content.description}</p>
      </div>
    </div>
  </div>
);

const HowItWorksSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex items-center justify-center">
      <div className="space-y-6 max-w-3xl w-full">
        {content.steps.map((step, i) => (
          <div key={i} className="flex items-center gap-6">
            <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 bg-white/5 rounded-xl p-5 border border-white/10">
              <span className="text-cyan-400 font-semibold">{step.action}</span>
              <span className="text-white/60 mx-3">→</span>
              <span className="text-white">{step.result}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const AppliesToSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex items-center justify-center">
      <div className="flex gap-8">
        {content.items.map((item, i) => (
          <div key={i} className="bg-white/5 rounded-xl p-8 flex flex-col items-center border border-white/10 min-w-[180px]">
            <IconComponent name={item.icon} size={48} className="text-orange-400 mb-4" />
            <span className="text-white text-lg font-semibold">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-6 border border-cyan-500/30">
      <p className="text-xl text-white text-center">{content.message}</p>
    </div>
  </div>
);

const TwoLensesSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex items-center justify-center gap-8">
      <div className="bg-white/5 rounded-xl overflow-hidden border border-red-500/30 w-72">
        <div className="p-4" style={{ backgroundColor: content.lensA.color }}>
          <h3 className="text-xl font-bold text-white text-center">{content.lensA.title}</h3>
        </div>
        <div className="p-6 space-y-3">
          {content.lensA.items.map((item, i) => (
            <div key={i} className="text-white/80 text-center text-lg">{item}</div>
          ))}
        </div>
      </div>
      <div className="text-4xl font-bold text-white/30">vs</div>
      <div className="bg-white/5 rounded-xl overflow-hidden border border-green-500/30 w-72">
        <div className="p-4" style={{ backgroundColor: content.lensB.color }}>
          <h3 className="text-xl font-bold text-white text-center">{content.lensB.title}</h3>
        </div>
        <div className="p-6 space-y-3">
          {content.lensB.items.map((item, i) => (
            <div key={i} className="text-white/80 text-center text-lg">{item}</div>
          ))}
        </div>
      </div>
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-orange-500">{content.punchline}</p>
    </div>
  </div>
);

const FactsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-4 gap-6">
      {content.stats.map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-6 flex flex-col items-center justify-center border border-orange-500/30">
          <span className="text-4xl font-bold text-orange-400 mb-2">{stat.value}</span>
          <span className="text-white/60 text-center text-sm">{stat.label}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-3xl font-bold text-cyan-400 italic">"{content.quote}"</p>
    </div>
  </div>
);

const WhatWeBuiltSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-6 max-w-4xl mx-auto">
      {content.achievements.map((item, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
          <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <IconComponent name={item.icon} size={24} className="text-green-400" />
          </div>
          <span className="text-white text-lg">{item.text}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-green-400">{content.punchline}</p>
    </div>
  </div>
);

const MomentumSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-6">
      {content.items.map((item, i) => (
        <div key={i} className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30 flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <IconComponent name={item.icon} size={28} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white mb-1">{item.label}</h3>
            <p className="text-white/60">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const OpportunitySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-8">
      {content.categories.map((cat, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 border border-white/10">
          <h3 className="text-xl font-bold text-orange-400 mb-4">{cat.title}</h3>
          <div className="space-y-3">
            {cat.items.map((item, j) => (
              <div key={j} className="flex items-center gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                <span className="text-white/80">{item}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-green-400">{content.punchline}</p>
    </div>
  </div>
);

const ReframeSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-4 gap-4">
      {content.columns.map((col, i) => (
        <div key={i} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
          <div className="p-4" style={{ backgroundColor: col.color }}>
            <p className="text-white text-center font-semibold">Focus on {col.focus}</p>
          </div>
          <div className="p-6 flex items-center justify-center min-h-[100px]">
            <p className="text-white/80 text-center">{col.result}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const QuestionSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="space-y-6 max-w-2xl">
        {content.questions.map((q, i) => (
          <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-6 border border-white/10">
            <Search size={24} className="text-orange-400 flex-shrink-0" />
            <span className="text-white text-xl">{q}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const IntentionalSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="space-y-6 max-w-2xl w-full">
        {content.actions.map((action, i) => (
          <div key={i} className="flex items-center gap-4 bg-gradient-to-r from-orange-500/20 to-orange-600/10 rounded-xl p-6 border border-orange-500/30">
            <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
              <IconComponent name={action.icon} size={24} className="text-white" />
            </div>
            <span className="text-white text-xl font-semibold">{action.text}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-orange-400">{content.punchline}</p>
    </div>
  </div>
);

const ClosingSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-12 opacity-90" />
    <p className="text-xl text-white/60 mb-4">{content.setup}</p>
    <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">"{content.statement}"</h2>
    <p className="text-xl text-white/60 mb-4">{content.question}</p>
    <h1 className="text-4xl md:text-5xl font-light text-white mb-2">{content.highlight}</h1>
    <h1 className="text-4xl md:text-5xl font-bold text-orange-500">{content.answer}</h1>
  </div>
);

// Main slide renderer
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    hook: HookSlide,
    filter: FilterSlide,
    howItWorks: HowItWorksSlide,
    appliesTo: AppliesToSlide,
    twoLenses: TwoLensesSlide,
    facts: FactsSlide,
    whatWeBuilt: WhatWeBuiltSlide,
    momentum: MomentumSlide,
    opportunity: OpportunitySlide,
    reframe: ReframeSlide,
    question: QuestionSlide,
    intentional: IntentionalSlide,
    closing: ClosingSlide,
  };
  
  const Renderer = renderers[slide.type];
  return Renderer ? <Renderer content={slide.content} /> : null;
};

export default function PresentationsFocus() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, toggleFullscreen, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      {/* Slide Container */}
      <div className="flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2240] to-[#0a1628]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(168, 85, 247, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 20%, rgba(34, 211, 238, 0.06) 0%, transparent 40%)
            `
          }}
        />
        
        {/* Current Slide */}
        <div className="relative h-full">
          <SlideRenderer slide={slides[currentSlide]} />
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronLeft className="text-white" size={24} />
        </button>
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronRight className="text-white" size={24} />
        </button>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#050d18] border-t border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">
            Slide {currentSlide + 1} of {slides.length}
          </span>
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentSlide ? "bg-orange-500 w-6" : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/40 text-xs">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">F</kbd> for fullscreen
            <span className="mx-2">|</span>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 ml-1">→</kbd> to navigate
          </span>
          <PresentationDownloadButton
            slides={slides}
            renderSlide={(s) => <SlideRenderer slide={s} />}
            filename="What-You-Focus-On"
            bgColor="#0a1628"
          />
          <button
            onClick={toggleFullscreen}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {isFullscreen ? "Exit Fullscreen" : "Present"}
          </button>
        </div>
      </div>
    </div>
  );
}
